import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Form, FormField, FormGroup, TextArea, Button, Icon, Checkbox, Message } from 'semantic-ui-react';
import { fetchWithAuth, getCSRFToken, isAssociateManagerOrAbove, canEditAll } from '../auth';
import { statusMap } from './helpers';

export const eventTypes = [
    { key: 'sick', value: 'sick', text: 'Sick' },
    { key: 'pto', value: 'pto', text: 'PTO' },
    { key: 'training', value: 'training', text: 'Training' },
    { key: 'unavailable', value: 'unavailable', text: 'Unavailable' },
    { key: 'teamEvent', value: 'teamEvent', text: 'Team Event' },
];

const VACATION_TYPES = ['pto', 'sick'];
const MAX_PTO_HOURS_PER_DAY = 8;

function getPtoConflictError(type, start, end, existingEvents, selfId, allDay = false) {
    if (!VACATION_TYPES.includes(type) || !start || !end || existingEvents.length === 0) return null;

    const newS = new Date(start);
    const newE = new Date(end);
    const relevant = existingEvents.filter(e => {
        const id = String(e.event_id).replace('member-', '');
        return (!selfId || id !== selfId) && VACATION_TYPES.includes(e.type);
    });

    const overlapping = relevant.find(e => newS < new Date(e.end) && newE > new Date(e.start));
    if (overlapping) return 'You already have PTO/Sick leave scheduled that overlaps with this time.';

    // All-day events count as one full slot — skip the hourly cap check.
    if (allDay) return null;

    const dayStart = new Date(newS); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(newS);   dayEnd.setHours(23, 59, 59, 999);
    const sameDayHours = relevant
        .filter(e => new Date(e.start) < dayEnd && new Date(e.end) > dayStart)
        .reduce((acc, e) => acc + (new Date(e.end) - new Date(e.start)) / 3_600_000, 0);
    const newHours = (newE - newS) / 3_600_000;

    if (sameDayHours + newHours > MAX_PTO_HOURS_PER_DAY) {
        const remaining = Math.max(0, MAX_PTO_HOURS_PER_DAY - sameDayHours);
        const fmt = (h) => h % 1 === 0 ? h : h.toFixed(1);
        return `Daily PTO/Sick limit reached. You have ${fmt(sameDayHours)}h already scheduled — only ${fmt(remaining)}h remaining (max ${MAX_PTO_HOURS_PER_DAY}h/day).`;
    }
    return null;
}

export function toISOStringWithTimezone(date) {
    const tzoffset = (new Date()).getTimezoneOffset() * 60000;
    return (new Date(date - tzoffset)).toISOString().slice(0, -1);
}

export function buildAllDayTimes(startDateStr, endDateStr) {
    const start = new Date(`${startDateStr}T00:00:00`);
    start.setHours(6, 0, 0, 0);
    const end = new Date(`${endDateStr || startDateStr}T00:00:00`);
    end.setHours(18, 0, 0, 0);
    return {
        start: toISOStringWithTimezone(start),
        end: toISOStringWithTimezone(end),
    };
}

/**
 * Shared calendar event editor used by both MemberSchedule and TeamCalendarView.
 *
 * Props:
 *   scheduler     – the scheduler object injected by @aldabil/react-scheduler's customEditor
 *   memberId      – the user_id of the member whose schedule is being edited
 *   eventId       – numeric ID of the event being edited (null when creating)
 *   titleField    – key on the scheduler event object that holds the display title
 *                   ('title' for MemberSchedule, 'eventTitle' for TeamCalendarView)
 *   onSuccess     – callback fired after a successful create or patch
 *   setError      – setter for the parent's error state
 *   onConfirm     – optional: if provided, called with (eventPayload, "create"|"edit")
 *                   after a successful save (used by MemberSchedule to update the scheduler)
 *   checkVacationLimit – optional function (start, end) => string|null from TeamCalendarView;
 *                   when provided, re-evaluated reactively as the user changes start/end
 */
export const ScheduleEventEditor = ({
    scheduler,
    memberId,
    eventId,
    titleField = 'title',
    onSuccess,
    setError,
    onConfirm,
    checkVacationLimit = null,
    existingEvents = [],
}) => {
    const event = scheduler.edited;
    const creating = !event;

    const [editorError, setEditorError] = useState('');
    const [saveError, setSaveError] = useState('');

    // In create mode cap the default end to start+4h to avoid an immediate daily-cap error.
    const defaultStart = scheduler.state.start.value;
    const maxCreateEnd = new Date(defaultStart.getTime() + 4 * 3_600_000);
    const defaultEnd = creating && scheduler.state.end.value > maxCreateEnd
        ? maxCreateEnd
        : scheduler.state.end.value;

    const [eventDetails, setEventDetails] = useState({
        event_id: scheduler.state.event_id.value,
        start: toISOStringWithTimezone(defaultStart),
        end: toISOStringWithTimezone(defaultEnd),
        title: event?.[titleField],
        notes: event?.notes,
        type: event?.type,
        allDay: event?.allDay ?? false,
        approvalStatus: event?.approvalStatus
            ? event.approvalStatus
            : (canEditAll() ? 'approved' : 'pending'),
        designatedApprover: event?.designatedApprover,
    });
    const [fieldErrors, setFieldErrors] = useState({});

    async function getSupervisor(id) {
        setEditorError('');
        const resp = await fetchWithAuth(
            `${process.env.REACT_APP_ICC_TOOL_API_HOST}members/${id}/`,
            { headers: { 'X-CSRFToken': getCSRFToken() } }
        );
        if (!resp.ok) {
            setEditorError('Something went wrong. Does this member exist?');
            return;
        }
        const respJson = await resp.json();
        handleChange('designatedApprover', respJson.supervisor);
    }

    useEffect(() => {
        if (creating) {
            getSupervisor(memberId);
        }
    }, []);

    async function saveEvent(payload) {
        const url = `${process.env.REACT_APP_ICC_TOOL_API_HOST}members/${memberId}/schedule/${creating ? '' : `${eventId}/`}`;
        const resp = await fetchWithAuth(url, {
            headers: { 'X-CSRFToken': getCSRFToken(), 'Content-Type': 'application/json' },
            credentials: 'include',
            method: creating ? 'POST' : 'PATCH',
            body: JSON.stringify(payload),
        });
        if (!resp.ok) {
            const text = await resp.text().catch(() => '');
            // Django debug pages and proxy errors return HTML — don't show them raw.
            if (!text.trim() || text.trimStart().startsWith('<')) {
                return 'An error occurred. Please reach out to your supervisor.';
            }
            try {
                const body = JSON.parse(text);
                const detail = body?.detail ?? body?.non_field_errors?.[0];
                if (detail) return Array.isArray(detail) ? detail[0] : detail;
            } catch { /* not JSON — fall through */ }
            return text.trim() || 'An error occurred. Please reach out to your supervisor.';
        }
        return null;
    }

    function handleChange(key, value) {
        let updates = { [key]: value };

        if (key === 'allDay') {
            if (value) {
                const startStr = eventDetails.start?.slice(0, 10) || new Date().toISOString().slice(0, 10);
                const endStr = eventDetails.end?.slice(0, 10) || startStr;
                const rangeEnd = endStr >= startStr ? endStr : startStr;
                const times = buildAllDayTimes(startStr, rangeEnd);
                updates = { start: times.start, end: times.end, allDay: true };
            } else {
                updates = { allDay: false };
            }
        } else if (key === 'start' && eventDetails.allDay) {
            const endStr = eventDetails.end?.slice(0, 10) || value;
            const rangeEnd = endStr >= value ? endStr : value;
            const times = buildAllDayTimes(value, rangeEnd);
            updates = { start: times.start, end: times.end };
        } else if (key === 'end' && eventDetails.allDay) {
            const startStr = eventDetails.start?.slice(0, 10) || value;
            const times = buildAllDayTimes(startStr, value);
            updates = { start: times.start, end: times.end };
        }

        if (key === 'start' || key === 'end') {
            updates.approvalStatus = isAssociateManagerOrAbove() ? 'approved' : 'pending';
        }

        setEventDetails(prev => ({ ...prev, ...updates }));
        setFieldErrors(prev => ({ ...prev, [key]: false }));
        setSaveError('');
    }

    async function handleSubmit() {
        const errors = {};
        for (const field of ['start', 'end', 'title', 'type']) {
            if (!eventDetails[field]) errors[field] = 'This field is required.';
        }
        if (Object.keys(errors).length) {
            setFieldErrors(errors);
            return;
        }
        if (ptoConflictError || isEndBeforeStart) return;

        const payload = {
            start: new Date(eventDetails.start).toISOString(),
            end: new Date(eventDetails.end).toISOString(),
            title: eventDetails.title,
            notes: eventDetails.notes,
            type: eventDetails.type,
            approvalStatus: eventDetails.approvalStatus,
            designatedApprover: eventDetails.designatedApprover?.userId,
        };

        let err;
        try {
            err = await saveEvent(payload);
        } catch {
            err = 'An error occurred. Please reach out to your supervisor.';
        }
        if (err) {
            setSaveError(err);
            return;
        }
        scheduler.close();
        onSuccess();
        if (onConfirm) {
            onConfirm(
                { ...payload, start: new Date(payload.start), end: new Date(payload.end) },
                creating ? 'create' : 'edit'
            );
        }
    }

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
    const midnightTonight = new Date(); midnightTonight.setHours(23, 59, 59, 999);
    const todayFormatted = new Date().toISOString().slice(0, 16);
    const todayDateOnly = new Date().toISOString().slice(0, 10);

    const isSameDayBlocked =
        VACATION_TYPES.includes(eventDetails.type) &&
        eventDetails.start &&
        new Date(eventDetails.start) < tomorrow &&
        !isAssociateManagerOrAbove();

    const isEndBeforeStart = eventDetails.allDay
        ? (eventDetails.start?.slice(0, 10) > eventDetails.end?.slice(0, 10))
        : (eventDetails.start > eventDetails.end);

    const vacationLimitError = creating && checkVacationLimit && !isEndBeforeStart
        ? checkVacationLimit(eventDetails.start, eventDetails.end)
        : null;

    const selfId = eventId != null ? String(eventId).replace('member-', '') : null;

    const ptoConflictError = getPtoConflictError(
        eventDetails.type, eventDetails.start, eventDetails.end, existingEvents, selfId, eventDetails.allDay
    );

    const isVacationTypeBlocked = !!vacationLimitError && VACATION_TYPES.includes(eventDetails.type);

    const futureThreshold = creating ? midnightTonight : today;
    const canSubmit =
        !isVacationTypeBlocked &&
        !ptoConflictError &&
        !isEndBeforeStart &&
        !isSameDayBlocked &&
        event?.type !== 'teamEvent' &&
        (new Date(eventDetails.start) > futureThreshold ||
         new Date(eventDetails.end) > futureThreshold ||
         isAssociateManagerOrAbove());

    const blockReason =
        isEndBeforeStart         ? 'End date must be on or after the start date.'
        : isVacationTypeBlocked  ? 'No Time Off Available — choose a different date'
        : isSameDayBlocked       ? 'PTO and Sick requests cannot be submitted for today — please select a future date'
        : ptoConflictError       ? 'Resolve the scheduling conflict above to submit'
        : event?.type !== 'teamEvent' ? 'Start or end must be greater than today'
        : null;

    if (editorError) {
        return <div style={{ fontWeight: 'bold', margin: '0.5rem' }}>{editorError}</div>;
    }

    return (
        <div id="create-event-modal">
            {saveError && (
                <Message negative>
                    <Message.Header>Could Not Save</Message.Header>
                    <p>{saveError}</p>
                </Message>
            )}
            {vacationLimitError && (
                <Message warning>
                    <Message.Header>No Time Off Available</Message.Header>
                    <p>{vacationLimitError}</p>
                </Message>
            )}
            {ptoConflictError && (
                <Message negative>
                    <Message.Header>Scheduling Conflict</Message.Header>
                    <p>{ptoConflictError}</p>
                </Message>
            )}
            <Form>
                <FormField required>
                    <label>Title</label>
                    <Form.Input
                        value={eventDetails.title}
                        onChange={(e, data) => handleChange('title', data.value)}
                        error={fieldErrors.title}
                    />
                </FormField>
                <FormField>
                    <label>Notes</label>
                    <TextArea
                        value={eventDetails.notes}
                        onChange={(e, data) => handleChange('notes', data.value)}
                    />
                </FormField>
                <FormGroup>
                    <FormField required width={8}>
                        <label>Type</label>
                        <Form.Dropdown
                            selection
                            options={eventTypes}
                            value={eventDetails.type}
                            onChange={(e, data) => handleChange('type', data.value)}
                            error={fieldErrors.type}
                        />
                    </FormField>
                    <FormField width={8} disabled>
                        <label style={{ opacity: '1' }}>Approval Status</label>
                        <Form.Input style={{ opacity: '1' }} value={statusMap[eventDetails.approvalStatus]} />
                    </FormField>
                </FormGroup>
                <FormField>
                    <label>Designated Approver</label>
                    <Form.Input className="icct-disabled-input" value={eventDetails.designatedApprover?.name} />
                </FormField>
                {eventDetails.designatedApprover && eventDetails.designatedApprover.status !== 'active' && (
                    <div style={{ marginBottom: '0.5rem' }}>
                        <Icon className="exclamation circle" color="red" />
                        <Link to={`/members/${eventDetails.designatedApprover?.id}/view`}>
                            {eventDetails.designatedApprover?.name}
                        </Link>{' '}
                        is out right now. You may contact your Associate Manager or secondary Managers and Associate Managers.
                    </div>
                )}
                <FormField>
                    <label>All Day</label>
                    <Checkbox
                        value={eventDetails.allDay}
                        checked={eventDetails.allDay}
                        onChange={(e, data) => handleChange('allDay', data.checked)}
                    />
                </FormField>
                <FormGroup>
                    <FormField>
                        <label>Start</label>
                        <Form.Input
                            min={eventDetails.allDay ? todayDateOnly : todayFormatted}
                            max={eventDetails.allDay ? undefined : `${eventDetails.start?.slice(0, 10) || todayDateOnly}T18:00`}
                            aria-label={eventDetails.allDay ? 'Date' : 'Date and time'}
                            type={eventDetails.allDay ? 'date' : 'datetime-local'}
                            value={eventDetails.allDay ? eventDetails.start?.slice(0, 10) : eventDetails.start}
                            onChange={(e, data) => handleChange('start', data.value)}
                        />
                    </FormField>
                    <FormField style={{ maxWidth: '20rem' }}>
                        <label>End</label>
                        <Form.Input
                            min={eventDetails.allDay
                                ? (eventDetails.start?.slice(0, 10) || todayDateOnly)
                                : (eventDetails.start || todayFormatted)}
                            max={eventDetails.allDay ? undefined : `${eventDetails.end?.slice(0, 10) || todayDateOnly}T18:00`}
                            aria-label={eventDetails.allDay ? 'Date' : 'Date and time'}
                            error={
                                isEndBeforeStart
                                    ? 'End date must be on or after the start date.'
                                    : null
                            }
                            type={eventDetails.allDay ? 'date' : 'datetime-local'}
                            value={eventDetails.allDay ? eventDetails.end?.slice(0, 10) : eventDetails.end}
                            onChange={(e, data) => handleChange('end', data.value)}
                        />
                    </FormField>
                </FormGroup>
            </Form>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                {canSubmit ? (
                    <FormField>
                        <Button
                            style={{ backgroundColor: 'var(--add)', color: 'white' }}
                            onClick={handleSubmit}
                        >
                            Submit
                        </Button>
                    </FormField>
                ) : blockReason && (
                    <FormField style={{ margin: '0.5rem' }}>
                        <Icon className="exclamation circle" />
                        {blockReason}
                    </FormField>
                )}
                <FormField>
                    <Button
                        style={{ backgroundColor: 'var(--delete)', color: 'white' }}
                        onClick={scheduler.close}
                    >
                        Cancel
                    </Button>
                </FormField>
            </div>
        </div>
    );
};
